/**
 * Minimal JWT payload reader. The access token is only *read* here for UI hints (which buttons to
 * show); it is never trusted for enforcement — the API validates the signature and authorizes every
 * call. So no signature check is needed or done.
 */

/** Decodes a JWT's payload to an object, or null if the token is malformed. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length < 3) {
    return null;
  }

  try {
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    base64 += '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(char => '%' + char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** The "role" claim values from a decoded payload. The claim may be a single string or an array. */
export function roleClaimValues(payload: Record<string, unknown> | null): string[] {
  const role = payload?.['role'];
  if (typeof role === 'string') {
    return [role];
  }
  if (Array.isArray(role)) {
    return role.filter((value): value is string => typeof value === 'string');
  }
  return [];
}
